/**
 * src/utils/osmBuildingFetcher.ts
 *
 * Fetches authoritative building geometry + per-tier heights for the
 * "Import 3D building" workflow directly from OpenStreetMap via the
 * Overpass API.
 *
 * Why this exists
 * ───────────────
 * Mapbox / MapTiler vector tiles are a *lossy*, simplified projection
 * of the original OSM data: building parts get merged / clipped /
 * re-tagged, and the tile generator collapses the vertical extent of
 * overlapping parts so a polygon whose true OSM tag is
 * `min_height=15, height=70` gets re-emitted with `min_height=0,
 * height=710`. That's the root cause of the "wide blue Y-wings
 * floating mid-tower" artefact on Burj Khalifa: heuristics on tile
 * data can't reconstruct what the tile pipeline already discarded.
 *
 * Pulling `building:part` polygons straight from OSM gives us the
 * authoritative `min_height` / `height` tags as the cartographers
 * entered them — no inference, no envelope-clipping, no fat-block
 * heuristics. Burj Khalifa's relation 3025428 has ~38 mapped parts;
 * each one becomes one tier in our spatial object, with exact
 * setbacks.
 *
 * Failure mode
 * ────────────
 * Overpass can be slow, rate-limited, or the queried building may
 * not be mapped with `building:part=*` polygons. In every such case
 * `fetchOsmBuildingParts` resolves to `null` and the caller is
 * expected to fall back to the existing Mapbox-tile heuristic
 * pipeline. Diagnostics are logged via the returned `diagnostics`
 * field so operators can see *why* OSM lookup didn't apply.
 */

import type { BuildingTier } from '../components/spatial/SpatialObjectsAdminOverlay';

/**
 * Two-stage data strategy
 * ───────────────────────
 *
 * 1. **OSM Buildings tile API** (primary, browser-friendly):
 *    `https://data.osmbuildings.org/0.2/anonymous/tile/{z}/{x}/{y}.json`
 *    serves CDN-hosted GeoJSON FeatureCollections of buildings with
 *    pre-extracted `height` / `minHeight` properties (camelCase, in
 *    metres). Same OSM source data as Overpass, but pre-tiled and
 *    served with proper CORS headers so a direct browser fetch
 *    works from any origin. No server hop needed — this is the
 *    fastest, most reliable path and should cover the vast majority
 *    of clicks.
 *
 * 2. **Overpass via same-origin proxy** (fallback): when the tile
 *    API has no features for the clicked spot (rural buildings,
 *    very recent additions not yet processed into tiles), we fall
 *    back to a live Overpass QL query through the Express
 *    `/api/overpass` proxy. We do NOT try the public Overpass
 *    mirrors directly from the browser — they reject the CORS
 *    preflight for text/plain POSTs (HTTP 406) so direct in-browser
 *    calls always fail.
 */

// Tile zoom 15 is the OSM Buildings standard; one tile is ~1.2km
// across at the equator, comfortably bigger than any single
// building's bbox. We fetch the click tile + 8 neighbours so a
// building straddling a tile edge still gets all its parts.
const OSM_BUILDINGS_TILE_BASE = 'https://data.osmbuildings.org/0.2/anonymous/tile';
const OSM_BUILDINGS_TILE_ZOOM = 15;

const OVERPASS_ENDPOINTS: Array<{ url: string; isProxy: boolean }> = [
  { url: '/api/overpass', isProxy: true },
];

/** Where an imported footprint came from. */
export type GeometrySource = 'openstreetmap' | 'osm-buildings-tiles' | 'mapbox-3d-tiles';

/**
 * OpenStreetMap data is ODbL, which asks for attribution wherever the data
 * is shown and for the licence to travel with any derived database. We
 * persist derived footprints into our own tables, so both halves apply:
 * `metadata.geometry_provenance` carries the machine-readable half and
 * this string is the half a person reads.
 *
 * All three sources trace back to OSM. OSM Buildings serves OSM data
 * directly; Mapbox's building layer is derived from it, which is why
 * Mapbox's own terms require the contributors credited alongside theirs.
 */
export const OSM_ODBL_LICENSE = 'ODbL-1.0';
export const OSM_LICENSE_URL = 'https://opendatacommons.org/licenses/odbl/1-0/';
export const OSM_ATTRIBUTION = '© OpenStreetMap contributors (ODbL)';

export function attributionFor(source: GeometrySource): string {
  return source === 'mapbox-3d-tiles'
    ? `© Mapbox ${OSM_ATTRIBUTION}`
    : OSM_ATTRIBUTION;
}

/**
 * One structure the import could include, with the evidence for it.
 *
 * Offered rather than applied. The towers of a complex are frequently
 * three separate top-level ways with nothing in OSM to say they belong
 * together — the Kuwait Towers capture has no `name`, no `wikidata` ref
 * and no relation of any kind within 450m — so the only honest basis for
 * grouping them is an operator looking at the evidence and deciding.
 * Importing by proximity alone would just as happily annex the mosque.
 */
export interface OsmStructureOption {
  /** OSM id of the outline, or of the widest section when none exists. */
  id: number;
  kind: 'way' | 'relation';
  /** `name:en` if present, else `name`. Usually absent. */
  name: string | null;
  /** Metres from the click to this structure's centre. */
  distanceMeters: number;
  /** True for the structure the click landed in. */
  clicked: boolean;
  polygons: number[][][][];
  tiers: BuildingTier[];
  heightMeters: number;
  baseHeightMeters: number;
  /** The least trustworthy provenance among this structure's sections. */
  heightProvenance: HeightProvenance;
  /** OSM ids of every section contributing a polygon. */
  osmIds: number[];
}

/**
 * Combine the structures an operator chose into one object's geometry.
 *
 * Indices are reassigned as the polygons are concatenated, because each
 * structure numbers its own extents from zero and a merged list that kept
 * the originals would have several parts all claiming polygon 0 — every
 * one of them extruding the first footprint.
 */
export function mergeStructures(selected: OsmStructureOption[]): {
  polygons: number[][][][];
  tiers: BuildingTier[];
  heightMeters: number;
  baseHeightMeters: number;
  osmIds: number[];
  heightProvenance: HeightProvenance;
} {
  const polygons: number[][][][] = [];
  const tiers: BuildingTier[] = [];
  const osmIds: number[] = [];
  const provenances: HeightProvenance[] = [];
  let maxH = -Infinity;
  let minB = Infinity;

  for (const s of selected) {
    const offset = polygons.length;
    for (const poly of s.polygons) polygons.push(poly);
    for (const t of s.tiers) {
      tiers.push({
        polygon_index: t.polygon_index + offset,
        min_height: t.min_height,
        height: t.height,
      });
      if (t.height > maxH) maxH = t.height;
      if (t.min_height < minB) minB = t.min_height;
    }
    for (const id of s.osmIds) osmIds.push(id);
    provenances.push(s.heightProvenance);
  }

  return {
    polygons,
    tiers,
    heightMeters: Number.isFinite(maxH) ? maxH : 0,
    baseHeightMeters: Number.isFinite(minB) ? minB : 0,
    osmIds,
    heightProvenance: weakestProvenance(provenances),
  };
}

/** Result of a successful OSM building-parts lookup. */
export interface OsmBuildingImport {
  /**
   * GeoJSON MultiPolygon coordinate tree — one outer ring per
   * `building:part` (or, in the no-parts fallback, a single ring
   * for the building shell). Each polygon corresponds to one tier
   * in `tiers` via the matching `polygon_index`.
   */
  polygons: number[][][][];
  /** Per-polygon (min_height, height) capture, exact OSM values. */
  tiers: BuildingTier[];
  /** max(tiers[].height) — fed into the form's height_meters field. */
  heightMeters: number;
  /** min(tiers[].min_height) — fed into base_height_meters. */
  baseHeightMeters: number;
  /**
   * Which path produced this result:
   *   - 'osm-buildings' — direct tile fetch from data.osmbuildings.org
   *   - 'osm-overpass'  — live Overpass QL query via /api/overpass
   *
   * Logged in the importBuilding console diagnostic so operators can
   * tell which data source backed any given record.
   */
  source: 'osm-buildings' | 'osm-overpass';
  /**
   * The least trustworthy provenance among the emitted sections, so a
   * building whose height is a storey-count guess cannot be presented as
   * though it were surveyed.
   */
  heightProvenance: HeightProvenance;
  /**
   * The clicked structure plus any neighbour the operator might mean to
   * include. Always contains the clicked structure. Never auto-merged:
   * `polygons` / `tiers` above describe the clicked structure alone.
   */
  structures: OsmStructureOption[];
  /** Free-form telemetry surfaced in the console for the operator. */
  diagnostics: {
    endpoint: string;
    rawElements: number;
    candidateParts: number;
    acceptedParts: number;
    /** OSM way id of the enclosing `building=*` shell, if found. */
    enclosingBuilding: number | null;
    /** OSM way ids of the parts/shell we actually used as tiers. */
    osmIds: number[];
    /** Human-readable notes for tag-parse misses, retries, etc. */
    notes: string[];
  };
}

/** Internal — Overpass way with inline geometry (`out geom`). */
interface OverpassWay {
  type: 'way';
  id: number;
  geometry: Array<{ lat: number; lon: number }>;
  tags?: Record<string, string>;
}

/** Internal — Overpass relation member with optional inline geometry. */
interface OverpassMember {
  type: 'way' | 'node' | 'relation';
  ref: number;
  role: string;
  geometry?: Array<{ lat: number; lon: number }>;
}

/** Internal — Overpass multipolygon / building relation (`out geom`). */
interface OverpassRelation {
  type: 'relation';
  id: number;
  tags?: Record<string, string>;
  members?: OverpassMember[];
}

type OverpassElement = OverpassWay | OverpassRelation;

/**
 * What counts as a structure worth importing.
 *
 * The Overpass selector and the acceptance test are both generated from
 * this one list, because the two drifting apart fails silently: elements
 * the query asks for but the filter rejects are fetched, waited on and
 * then dropped, and the only trace is a raw element count that never
 * becomes a polygon.
 *
 * `man_made=tower` is here because a tower is frequently mapped with no
 * `building=*` tag at all — it is a structure, not a building, to the
 * people who map it — and a query that only asks about buildings cannot
 * see it however close the click lands.
 */
const STRUCTURE_SELECTORS: ReadonlyArray<{
  key: string;
  value?: string;
  /** A vertical section of something larger, or a standalone outline. */
  role: 'part' | 'shell';
}> = [
  { key: 'building:part', role: 'part' },
  { key: 'building', role: 'shell' },
  { key: 'man_made', value: 'tower', role: 'shell' },
  { key: 'man_made', value: 'water_tower', role: 'shell' },
];

/** `["k"]` or `["k"="v"]`, as Overpass QL wants it. */
function selectorToQl(s: { key: string; value?: string }): string {
  return s.value == null ? `["${s.key}"]` : `["${s.key}"="${s.value}"]`;
}

/**
 * The name the proxy knows a selector by.
 *
 * Must match the keys of `OVERPASS_KINDS` in server/index.js. The two
 * lists are checked against each other by a test rather than shared,
 * because no import crosses the server/client boundary in this repo.
 */
function selectorToKind(s: { key: string; value?: string }): string {
  return s.value == null ? s.key : `${s.key}=${s.value}`;
}

/** True when `tags` satisfies the selector. */
function tagsMatchSelector(
  tags: Record<string, string>,
  s: { key: string; value?: string },
): boolean {
  return s.value == null ? tags[s.key] != null : tags[s.key] === s.value;
}

/** One capture unit: one-or-more polygons sharing the same height tags. */
interface OsmPartCandidate {
  id: number;
  kind: 'way' | 'relation';
  /** Each entry is a Polygon coordinate array (outer + optional holes). */
  polygons: number[][][][];
  height: number;
  minHeight: number;
  tags: Record<string, string>;
  bbox: { w: number; s: number; e: number; n: number };
  centroid: { lng: number; lat: number };
  isBuildingPart: boolean;
  isBuildingShell: boolean;
  heightProvenance: HeightProvenance;
  minHeightProvenance: HeightProvenance;
}

/**
 * Parse OSM length tags into metres. Handles plain numbers, "585",
 * "585 m", "585m", "1,920 ft", "10'" (feet via apostrophe), and
 * comma decimals. Returns `null` for anything we can't safely
 * interpret — the caller treats that as "unknown" and falls back to
 * `building:levels` * 3.5 or rejects the part outright.
 */
const parseLengthMeters = (raw: unknown): number | null => {
  if (raw == null) return null;
  if (typeof raw === 'number' && Number.isFinite(raw)) return raw;
  const s = String(raw).trim();
  if (!s) return null;
  // Accept: "585", "585 m", "585m", "1,920 ft", "10'", "585.5"
  const m = s.match(/^(-?[\d.,]+)\s*(m|ft|')?\s*$/i);
  if (!m) return null;
  const n = Number(m[1].replace(/,/g, ''));
  if (!Number.isFinite(n)) return null;
  const unit = (m[2] ?? 'm').toLowerCase();
  if (unit === 'ft' || unit === "'") return n * 0.3048;
  return n;
};

/**
 * Approximate height from a `building:levels` tag. Standard rule of
 * thumb is ~3.5m per level (residential ~3m, office ~4m); we pick
 * the middle so the fallback is reasonable for either. Used only
 * when neither `height` nor `est_height` is set on the OSM feature.
 */
const heightFromLevels = (raw: unknown): number | null => {
  const n = Number(raw);
  if (!Number.isFinite(n) || n <= 0) return null;
  return n * 3.5;
};

type BBox = { w: number; s: number; e: number; n: number };

const bboxOfCoords = (coords: unknown): BBox | null => {
  let west = Infinity;
  let east = -Infinity;
  let south = Infinity;
  let north = -Infinity;
  const visit = (c: unknown): void => {
    if (!Array.isArray(c)) return;
    if (typeof c[0] === 'number' && typeof c[1] === 'number') {
      const lng = c[0] as number;
      const lat = c[1] as number;
      if (lng < west) west = lng;
      if (lng > east) east = lng;
      if (lat < south) south = lat;
      if (lat > north) north = lat;
      return;
    }
    for (const cc of c) visit(cc);
  };
  visit(coords);
  if (!Number.isFinite(west)) return null;
  return { w: west, s: south, e: east, n: north };
};

const centroidOfCoords = (coords: unknown): { lng: number; lat: number } | null => {
  let sumLng = 0;
  let sumLat = 0;
  let count = 0;
  const visit = (c: unknown): void => {
    if (!Array.isArray(c)) return;
    if (typeof c[0] === 'number' && typeof c[1] === 'number') {
      sumLng += c[0] as number;
      sumLat += c[1] as number;
      count++;
      return;
    }
    for (const cc of c) visit(cc);
  };
  visit(coords);
  if (count === 0) return null;
  return { lng: sumLng / count, lat: sumLat / count };
};

/**
 * Standard ray-casting point-in-polygon test. Used to find the
 * `building=*` shell that contains the operator's click point so we
 * can disambiguate the target building from neighbouring blocks
 * that the radius query also returned.
 */
const pointInRing = (
  lng: number,
  lat: number,
  ring: Array<[number, number]>,
): boolean => {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const [xi, yi] = ring[i];
    const [xj, yj] = ring[j];
    const intersect =
      ((yi > lat) !== (yj > lat)) &&
      (lng < ((xj - xi) * (lat - yi)) / (yj - yi + 1e-12) + xi);
    if (intersect) inside = !inside;
  }
  return inside;
};

/**
 * Convert lat/lon points into a closed GeoJSON ring `[lng, lat][]`.
 */
const latLonToRing = (
  geom: Array<{ lat: number; lon: number }>,
): Array<[number, number]> => {
  const ring: Array<[number, number]> = geom.map((p) => [p.lon, p.lat]);
  if (ring.length > 0) {
    const first = ring[0];
    const last = ring[ring.length - 1];
    if (first[0] !== last[0] || first[1] !== last[1]) {
      ring.push([first[0], first[1]]);
    }
  }
  return ring;
};

const ringIsClosed = (ring: Array<[number, number]>, eps = 1e-9): boolean => {
  if (ring.length < 4) return false;
  const a = ring[0];
  const b = ring[ring.length - 1];
  return Math.abs(a[0] - b[0]) <= eps && Math.abs(a[1] - b[1]) <= eps;
};

/**
 * Greedily stitch open ways that share endpoints into closed rings.
 * OSM multipolygon relations often split one outer ring across several
 * ways — without this we drop those parts entirely.
 */
const stitchOpenWaysToRings = (
  open: Array<Array<[number, number]>>,
): Array<Array<[number, number]>> => {
  const eps = 1e-7;
  const near = (a: [number, number], b: [number, number]) =>
    Math.abs(a[0] - b[0]) <= eps && Math.abs(a[1] - b[1]) <= eps;

  const remaining = open.map((r) => r.slice());
  const closed: Array<Array<[number, number]>> = [];

  while (remaining.length > 0) {
    let chain = remaining.shift()!;
    let grew = true;
    while (grew) {
      grew = false;
      if (ringIsClosed(chain, eps)) break;
      const head = chain[0];
      const tail = chain[chain.length - 1];
      for (let i = 0; i < remaining.length; i++) {
        const cand = remaining[i];
        const cHead = cand[0];
        const cTail = cand[cand.length - 1];
        if (near(tail, cHead)) {
          chain = chain.concat(cand.slice(1));
          remaining.splice(i, 1);
          grew = true;
          break;
        }
        if (near(tail, cTail)) {
          chain = chain.concat(cand.slice(0, -1).reverse());
          remaining.splice(i, 1);
          grew = true;
          break;
        }
        if (near(head, cTail)) {
          chain = cand.slice(0, -1).concat(chain);
          remaining.splice(i, 1);
          grew = true;
          break;
        }
        if (near(head, cHead)) {
          chain = cand.slice(1).reverse().concat(chain);
          remaining.splice(i, 1);
          grew = true;
          break;
        }
      }
    }
    if (!ringIsClosed(chain, eps) && chain.length >= 3) {
      chain = chain.concat([[chain[0][0], chain[0][1]]]);
    }
    if (ringIsClosed(chain, eps) && chain.length >= 4) closed.push(chain);
  }
  return closed;
};

/**
 * Assemble Polygon coordinate arrays from an Overpass multipolygon
 * relation. Every outer ring becomes its own polygon (MultiPolygon
 * parts of a single building:part). Inner rings are attached as holes
 * when their centroid falls inside an outer.
 */
const relationToPolygons = (rel: OverpassRelation): number[][][][] => {
  const closedOuters: Array<Array<[number, number]>> = [];
  const openOuters: Array<Array<[number, number]>> = [];
  const inners: Array<Array<[number, number]>> = [];

  for (const m of rel.members ?? []) {
    if (m.type !== 'way' || !Array.isArray(m.geometry) || m.geometry.length < 2) continue;
    const ring = latLonToRing(m.geometry);
    const role = (m.role || 'outer').toLowerCase();
    if (role === 'inner') {
      if (ringIsClosed(ring)) inners.push(ring);
      continue;
    }
    // outer / empty / outline
    if (ringIsClosed(ring)) closedOuters.push(ring);
    else openOuters.push(ring);
  }

  const stitched = stitchOpenWaysToRings(openOuters);
  const outers = closedOuters.concat(stitched);
  if (outers.length === 0) return [];

  return outers.map((outer) => {
    const holes = inners.filter((inner) => {
      const c = centroidOfCoords(inner);
      return c ? pointInRing(c.lng, c.lat, outer) : false;
    });
    return [outer, ...holes];
  });
};

/**
 * How much a height is worth trusting.
 *
 * `measured` — OSM states the height outright (`height`, `min_height`).
 * `inferred` — we derived it, from a storey count or from OSM's own
 *   `est_height`, which is an estimate by the tag's definition.
 * `unknown` — nothing in the tags says anything about it.
 *
 * Kept separate from the number so the operator can see which parts of a
 * building are survey data and which are a rule of thumb. Collapsing them
 * into one field is how a 3.5m-per-storey guess ends up presented with
 * the same authority as a surveyed 187m spire.
 */
export type HeightProvenance = 'measured' | 'inferred' | 'unknown';

type MeasuredHeight = { meters: number; provenance: HeightProvenance };

const heightWithProvenance = (tags: Record<string, string>): MeasuredHeight => {
  const stated = parseLengthMeters(tags.height);
  if (stated != null) return { meters: stated, provenance: 'measured' };

  // `est_height` is an estimate on OSM's own terms, so it is grouped with
  // our storey arithmetic rather than with a surveyed figure.
  const estimated = parseLengthMeters(tags['est_height']);
  if (estimated != null) return { meters: estimated, provenance: 'inferred' };

  const fromLevels = heightFromLevels(tags['building:levels']);
  if (fromLevels != null) return { meters: fromLevels, provenance: 'inferred' };

  return { meters: 0, provenance: 'unknown' };
};

const minHeightWithProvenance = (tags: Record<string, string>): MeasuredHeight => {
  const stated = parseLengthMeters(tags.min_height);
  if (stated != null) return { meters: stated, provenance: 'measured' };

  const fromLevels = heightFromLevels(tags['building:min_level']);
  if (fromLevels != null) return { meters: fromLevels, provenance: 'inferred' };

  // Absent `min_height` genuinely means the ground for the overwhelming
  // majority of buildings, so zero is the right number — but it was not
  // measured, and a part that should float is the thing most likely to be
  // wrong here.
  return { meters: 0, provenance: 'unknown' };
};

/** The least trustworthy provenance in a set — what the whole is worth. */
const weakestProvenance = (all: HeightProvenance[]): HeightProvenance => {
  if (all.some((p) => p === 'unknown')) return 'unknown';
  if (all.some((p) => p === 'inferred')) return 'inferred';
  return 'measured';
};

/**
 * Which of a structure's candidates actually become extrusions.
 *
 * `building:part` sections win when any of them carry a height: they are
 * the detailed mapping, and the shell around them is the envelope they
 * replace. Without them the shell itself is all there is.
 */
const sectionsToEmit = (
  inside: OsmPartCandidate[],
  outline: OsmPartCandidate,
): OsmPartCandidate[] => {
  const withHeight = inside.filter((c) => c.isBuildingPart && c.height > 0);
  if (withHeight.length > 0) return withHeight;
  const anyHeighted = inside.filter((c) => c.height > 0);
  if (anyHeighted.length > 0) return anyHeighted;
  return outline.height > 0 ? [outline] : [];
};

/** Flatten sections into one polygon list with a per-polygon extent each. */
const flattenSections = (sections: OsmPartCandidate[]): {
  polygons: number[][][][];
  tiers: BuildingTier[];
  heightMeters: number;
  baseHeightMeters: number;
  osmIds: number[];
  provenance: HeightProvenance;
  polysFromRelations: number;
} => {
  const polygons: number[][][][] = [];
  const tiers: BuildingTier[] = [];
  const osmIds: number[] = [];
  const provenances: HeightProvenance[] = [];
  let maxH = -Infinity;
  let minB = Infinity;
  let polysFromRelations = 0;

  for (const part of sections) {
    osmIds.push(part.id);
    for (const poly of part.polygons) {
      if (!poly?.[0] || poly[0].length < 4) continue;
      polygons.push(poly);
      tiers.push({
        polygon_index: polygons.length - 1,
        min_height: part.minHeight,
        height: part.height,
      });
      provenances.push(part.heightProvenance);
      if (part.height > maxH) maxH = part.height;
      if (part.minHeight < minB) minB = part.minHeight;
      if (part.kind === 'relation') polysFromRelations++;
    }
  }

  return {
    polygons,
    tiers,
    heightMeters: Number.isFinite(maxH) ? maxH : 0,
    baseHeightMeters: Number.isFinite(minB) ? minB : 0,
    osmIds,
    provenance: weakestProvenance(provenances),
    polysFromRelations,
  };
};

/** `name:en` if present, else `name`. */
const nameOf = (c: OsmPartCandidate): string | null =>
  c.tags['name:en']?.trim() || c.tags.name?.trim() || null;

/** Great-circle-ish metres between two lng/lat pairs at this latitude. */
const metresBetween = (
  aLng: number, aLat: number,
  bLng: number, bLat: number,
): number => {
  const mPerDegLat = 111_320;
  const mPerDegLng = mPerDegLat * Math.cos((aLat * Math.PI) / 180);
  return Math.hypot((bLng - aLng) * mPerDegLng, (bLat - aLat) * mPerDegLat);
};

/**
 * How far a neighbouring structure may sit and still be worth offering.
 *
 * Wide enough to reach the far tower of a landmark complex, narrow enough
 * that the list stays something a person can read. Nothing in it is
 * selected automatically, so being a little generous costs only screen
 * space; being too tight would hide the tower the operator came for.
 */
const NEIGHBOUR_RADIUS_M = 150;
const NEIGHBOUR_LIMIT = 12;

/** Point inside any polygon of a candidate (outer rings only). */
const pointInCandidate = (lng: number, lat: number, c: OsmPartCandidate): boolean => {
  for (const poly of c.polygons) {
    const outer = poly[0] as Array<[number, number]> | undefined;
    if (outer && pointInRing(lng, lat, outer)) return true;
  }
  return false;
};

/**
 * A cantilever or overhang puts a part's centroid outside the outline it
 * belongs to, so containment also accepts a part whose own outline is
 * mostly within the shell.
 */
const CONTAINMENT_VERTEX_SHARE = 0.6;

/**
 * True when `part` belongs to the building outlined by `shell`.
 *
 * Membership is geometric containment, never proximity. A `building:part`
 * sits inside its building's outline by OSM convention, while a
 * neighbour across the street does not, however close its bounding box
 * comes. Deciding this by padded bbox overlap is what let one click walk
 * from the clicked tower into its neighbours and then into theirs.
 */
const candidateIsInsideShell = (
  part: OsmPartCandidate,
  shell: OsmPartCandidate,
): boolean => {
  if (part === shell) return true;

  const outers: Array<Array<[number, number]>> = [];
  for (const poly of shell.polygons) {
    const outer = poly[0] as Array<[number, number]> | undefined;
    if (outer && outer.length >= 4) outers.push(outer);
  }
  if (outers.length === 0) return false;

  // Cheap common case: setbacks, cores and spires all centre inside the
  // outline they belong to.
  for (const outer of outers) {
    if (pointInRing(part.centroid.lng, part.centroid.lat, outer)) return true;
  }

  let inside = 0;
  let total = 0;
  for (const poly of part.polygons) {
    const ring = poly[0] as Array<[number, number]> | undefined;
    if (!ring) continue;
    for (const [lng, lat] of ring) {
      total++;
      if (outers.some((outer) => pointInRing(lng, lat, outer))) inside++;
    }
  }
  return total > 0 && inside / total >= CONTAINMENT_VERTEX_SHARE;
};

/**
 * Normalize a way or relation element into zero-or-more polygon parts
 * with height tags. Relations yield one candidate spanning all outers
 * (each outer kept as its own polygon under the same height).
 */
const elementToCandidate = (el: OverpassElement): OsmPartCandidate | null => {
  const tags = el.tags ?? {};
  const matched = STRUCTURE_SELECTORS.filter((s) => tagsMatchSelector(tags, s));
  if (matched.length === 0) return null;
  // A part tag wins over a shell tag on the same element: a tagged
  // section describes its own slice of a building, and treating it as an
  // outline would make it the boundary its siblings are tested against.
  const isBuildingPart = matched.some((s) => s.role === 'part');
  const isBuildingShell = !isBuildingPart && matched.some((s) => s.role === 'shell');

  let polygons: number[][][][] = [];
  if (el.type === 'way') {
    if (!Array.isArray(el.geometry) || el.geometry.length < 3) return null;
    const ring = latLonToRing(el.geometry);
    if (ring.length < 4) return null;
    polygons = [[ring]];
  } else {
    polygons = relationToPolygons(el);
  }
  if (polygons.length === 0) return null;

  const height = heightWithProvenance(tags);
  const minHeight = minHeightWithProvenance(tags);
  // Keep shells/parts even without height for enclosure tests; height
  // is required later when emitting tiers.
  const bbox = bboxOfCoords(polygons);
  const centroid = centroidOfCoords(polygons);
  if (!bbox || !centroid) return null;

  const usableHeight = height.meters > 0 ? height.meters : 0;
  const usableMin = Number.isFinite(minHeight.meters) && minHeight.meters >= 0
    ? minHeight.meters
    : 0;

  return {
    id: el.id,
    kind: el.type,
    polygons,
    height: usableHeight,
    minHeight: usableMin,
    tags,
    bbox,
    centroid,
    isBuildingPart,
    isBuildingShell,
    // A height we could not parse at all is unknown however the tag read.
    heightProvenance: usableHeight > 0 ? height.provenance : 'unknown',
    minHeightProvenance: minHeight.provenance,
  };
};

/**
 * Query OpenStreetMap via the Overpass API for the building at a
 * given (lng, lat) and return its `building:part` polygons with
 * exact OSM `min_height` / `height` tags.
 *
 * Fetches both ways AND multipolygon relations — complex hotels /
 * towers (Atlantis The Royal, Burj Khalifa, …) map many parts as
 * relations; the old way-only query dropped those entirely.
 */
async function fetchFromOverpass(
  lng: number,
  lat: number,
  opts: {
    searchRadiusM?: number;
    timeoutMs?: number;
    signal?: AbortSignal;
  } = {},
): Promise<OsmBuildingImport | null> {
  // 450m covers large resort footprints (Atlantis) without pulling the
  // entire neighbouring city block.
  const radiusM = opts.searchRadiusM ?? 450;
  const timeoutMs = opts.timeoutMs ?? 40_000;

  // A structured lookup, not Overpass QL. The proxy composes the query
  // from an allowlist of these same selectors, so the browser never needs
  // to be trusted with query text — see the route in server/index.js.
  const lookup = {
    lat,
    lng,
    radiusMeters: radiusM,
    kinds: STRUCTURE_SELECTORS.map(selectorToKind),
  };
  // Only for the dev case of pointing the app straight at a public mirror.
  const directQuery = `
[out:json][timeout:45];
(
${STRUCTURE_SELECTORS.flatMap((s) => {
    const ql = selectorToQl(s);
    return [
      `  way(around:${radiusM},${lat},${lng})${ql};`,
      `  relation(around:${radiusM},${lat},${lng})${ql};`,
    ];
  }).join('\n')}
);
out geom tags;
`.trim();

  const notes: string[] = [];
  let json: { elements?: OverpassElement[] } | null = null;
  let usedEndpoint = '';

  for (const { url, isProxy } of OVERPASS_ENDPOINTS) {
    const ctl = new AbortController();
    const fwd = opts.signal;
    const onAbort = () => ctl.abort();
    if (fwd) {
      if (fwd.aborted) ctl.abort();
      else fwd.addEventListener('abort', onAbort, { once: true });
    }
    const timer = setTimeout(() => ctl.abort(), timeoutMs);
    try {
      const res = await fetch(url, {
        method: 'POST',
        headers: isProxy
          ? { 'Content-Type': 'application/json' }
          : { 'Content-Type': 'text/plain;charset=UTF-8' },
        body: isProxy ? JSON.stringify(lookup) : directQuery,
        signal: ctl.signal,
      });
      clearTimeout(timer);
      if (fwd) fwd.removeEventListener('abort', onAbort);
      if (!res.ok) {
        let detail = '';
        if (isProxy) {
          try {
            const errBody = (await res.json()) as {
              error?: string;
              attempts?: Array<{
                endpoint: string;
                status?: number;
                error?: string;
                elapsed_ms?: number;
              }>;
            };
            if (Array.isArray(errBody.attempts)) {
              detail = ` — ${errBody.attempts
                .map((a) =>
                  `${a.endpoint.replace(/^https?:\/\//, '')}: ${
                    a.status != null ? `HTTP ${a.status}` : a.error || 'err'
                  }${a.elapsed_ms != null ? ` (${a.elapsed_ms}ms)` : ''}`,
                )
                .join('; ')}`;
            } else if (errBody.error) {
              detail = ` — ${errBody.error}`;
            }
          } catch { /* leave detail blank */ }
        }
        notes.push(`${url}: HTTP ${res.status}${detail}`);
        continue;
      }
      json = (await res.json()) as { elements?: OverpassElement[] };
      usedEndpoint = url;
      break;
    } catch (e) {
      clearTimeout(timer);
      if (fwd) fwd.removeEventListener('abort', onAbort);
      notes.push(`${url}: ${(e as Error).message}`);
    }
  }

  if (!json) return null;

  const rawElements = json.elements ?? [];
  const candidates: OsmPartCandidate[] = [];
  let wayCount = 0;
  let relCount = 0;
  for (const el of rawElements) {
    if (!el || (el.type !== 'way' && el.type !== 'relation')) continue;
    if (el.type === 'way') wayCount++;
    else relCount++;
    const c = elementToCandidate(el);
    if (c) candidates.push(c);
  }
  notes.push(`parsed ${candidates.length} candidates (${wayCount} ways, ${relCount} relations)`);

  const shells = candidates.filter((c) => c.isBuildingShell);
  const parts = candidates.filter((c) => c.isBuildingPart);

  // The clicked building's outline. A `building=*` shell is preferred:
  // it is the boundary every one of its `building:part` polygons sits
  // inside, and taking the smallest containing shell picks the tower
  // over the resort outline when both contain the point.
  let enclosing: OsmPartCandidate | null = null;
  let enclosingArea = Infinity;
  for (const c of shells) {
    if (!pointInCandidate(lng, lat, c)) continue;
    const area = (c.bbox.e - c.bbox.w) * (c.bbox.n - c.bbox.s);
    if (area < enclosingArea) {
      enclosingArea = area;
      enclosing = c;
    }
  }

  // No shell mapped here: fall back to the *widest* containing part,
  // normally the ground-level footprint the setbacks stack on top of.
  // Taking the smallest would pick a spire and then exclude the rest of
  // its own building for not fitting inside it.
  if (!enclosing) {
    let widest = -Infinity;
    for (const c of parts) {
      if (!pointInCandidate(lng, lat, c)) continue;
      const area = (c.bbox.e - c.bbox.w) * (c.bbox.n - c.bbox.s);
      if (area > widest) {
        widest = area;
        enclosing = c;
      }
    }
    if (enclosing) {
      notes.push('no building shell contains the click — using widest containing part as the outline');
    }
  }

  if (!enclosing) {
    notes.push('no building/part contains the click');
    return null;
  }

  // Everything that sits inside that outline, and nothing that merely
  // sits near it. A multipolygon relation contributes all of its outers
  // as the boundary, so its wings are covered without any union growth.
  const outline = enclosing;
  const accepted = candidates.filter((c) => candidateIsInsideShell(c, outline));
  notes.push(
    `${accepted.length}/${candidates.length} candidates inside the clicked outline ` +
    `(${outline.kind} ${outline.id})`,
  );

  const emitParts = sectionsToEmit(accepted, outline);
  if (emitParts.length === 1 && emitParts[0] === outline) {
    notes.push('fell back to enclosing shell (no heighted parts in union)');
  }
  if (emitParts.length === 0) {
    notes.push('no heighted polygons in growing union');
    return null;
  }

  // Every polygon of every section becomes its own extent entry, so a
  // MultiPolygon `building:part` is fully captured rather than collapsed.
  const clicked = flattenSections(emitParts);
  if (clicked.polygons.length === 0) return null;
  notes.push(
    `emitted ${clicked.polygons.length} polygons from ${emitParts.length} parts ` +
    `(${clicked.polysFromRelations} polys from relations)`,
  );

  // Neighbours worth offering: any other outline near the click, with its
  // own sections resolved the same way. Offered, never merged — see
  // `OsmStructureOption`.
  const neighbours: OsmStructureOption[] = [];
  for (const shell of shells) {
    if (shell === outline) continue;
    // A section of the clicked structure is not a neighbour of it.
    if (candidateIsInsideShell(shell, outline)) continue;
    const distanceMeters = metresBetween(
      lng, lat, shell.centroid.lng, shell.centroid.lat,
    );
    if (distanceMeters > NEIGHBOUR_RADIUS_M) continue;

    const inside = candidates.filter((c) => candidateIsInsideShell(c, shell));
    const sections = sectionsToEmit(inside, shell);
    if (sections.length === 0) continue;
    const flat = flattenSections(sections);
    if (flat.polygons.length === 0) continue;

    neighbours.push({
      id: shell.id,
      kind: shell.kind,
      name: nameOf(shell),
      distanceMeters,
      clicked: false,
      polygons: flat.polygons,
      tiers: flat.tiers,
      heightMeters: flat.heightMeters,
      baseHeightMeters: flat.baseHeightMeters,
      heightProvenance: flat.provenance,
      osmIds: flat.osmIds,
    });
  }
  neighbours.sort((a, b) => a.distanceMeters - b.distanceMeters);

  const structures: OsmStructureOption[] = [
    {
      id: outline.id,
      kind: outline.kind,
      name: nameOf(outline),
      distanceMeters: metresBetween(
        lng, lat, outline.centroid.lng, outline.centroid.lat,
      ),
      clicked: true,
      polygons: clicked.polygons,
      tiers: clicked.tiers,
      heightMeters: clicked.heightMeters,
      baseHeightMeters: clicked.baseHeightMeters,
      heightProvenance: clicked.provenance,
      osmIds: clicked.osmIds,
    },
    ...neighbours.slice(0, NEIGHBOUR_LIMIT),
  ];
  notes.push(
    `${structures.length - 1} neighbouring structures within ${NEIGHBOUR_RADIUS_M}m `
    + 'offered for selection (none auto-included)',
  );

  return {
    polygons: clicked.polygons,
    tiers: clicked.tiers,
    heightMeters: clicked.heightMeters,
    baseHeightMeters: clicked.baseHeightMeters,
    source: 'osm-overpass',
    heightProvenance: clicked.provenance,
    structures,
    diagnostics: {
      endpoint: usedEndpoint,
      rawElements: rawElements.length,
      candidateParts: parts.length,
      acceptedParts: clicked.polygons.length,
      enclosingBuilding: enclosing?.id ?? null,
      osmIds: clicked.osmIds,
      notes,
    },
  };
}

// ─── OSM Buildings tile path ──────────────────────────────────────────────
//
// data.osmbuildings.org publishes the same OSM building data Overpass
// would return, but pre-extracted into GeoJSON tiles served from a
// CDN with proper CORS headers. That makes it the ideal browser-side
// path: no proxy hop, no rate-limit games, no CORS preflight 406s.
//
// Tile coordinate reference: standard XYZ slippy-map scheme. Building
// data is available at zoom 15 only (per their docs); fixedZoom=15.

interface OsmBuildingsFeature {
  type: 'Feature';
  id?: string | number;
  geometry: {
    type: 'Polygon' | 'MultiPolygon';
    coordinates: number[][][] | number[][][][];
  };
  properties?: {
    height?: number;
    minHeight?: number;
    levels?: number;
    minLevel?: number;
    [k: string]: unknown;
  };
}

interface OsmBuildingsTile {
  type: 'FeatureCollection';
  features: OsmBuildingsFeature[];
}

/** Slippy-map tile coords for a (lng, lat) at the given zoom. */
const lngLatToTile = (
  lng: number,
  lat: number,
  z: number,
): { x: number; y: number; z: number } => {
  const n = 2 ** z;
  const x = Math.floor(((lng + 180) / 360) * n);
  const latRad = (lat * Math.PI) / 180;
  const y = Math.floor(
    ((1 - Math.log(Math.tan(latRad) + 1 / Math.cos(latRad)) / Math.PI) / 2) * n,
  );
  return { x, y, z };
};

/** Bbox of any GeoJSON Polygon / MultiPolygon coordinate tree. */
const geojsonBbox = (
  coords: unknown,
): { w: number; s: number; e: number; n: number } | null => {
  let west = Infinity;
  let east = -Infinity;
  let south = Infinity;
  let north = -Infinity;
  const visit = (c: unknown): void => {
    if (!Array.isArray(c)) return;
    if (typeof c[0] === 'number' && typeof c[1] === 'number') {
      const lng = c[0] as number;
      const lat = c[1] as number;
      if (lng < west) west = lng;
      if (lng > east) east = lng;
      if (lat < south) south = lat;
      if (lat > north) north = lat;
      return;
    }
    for (const cc of c) visit(cc);
  };
  visit(coords);
  if (!Number.isFinite(west)) return null;
  return { w: west, s: south, e: east, n: north };
};

/** Centroid of a coord tree (just the average of all sampled points). */
const geojsonCentroid = (
  coords: unknown,
): { lng: number; lat: number } | null => {
  let sumLng = 0;
  let sumLat = 0;
  let count = 0;
  const visit = (c: unknown): void => {
    if (!Array.isArray(c)) return;
    if (typeof c[0] === 'number' && typeof c[1] === 'number') {
      sumLng += c[0] as number;
      sumLat += c[1] as number;
      count++;
      return;
    }
    for (const cc of c) visit(cc);
  };
  visit(coords);
  if (count === 0) return null;
  return { lng: sumLng / count, lat: sumLat / count };
};

/** Point-in-polygon (any of the rings of a Polygon / MultiPolygon). */
const pointInPolygonOrMulti = (
  lng: number,
  lat: number,
  geom: { type: string; coordinates: unknown },
): boolean => {
  if (geom.type === 'Polygon') {
    const rings = geom.coordinates as number[][][];
    if (rings.length === 0) return false;
    const outer = rings[0] as Array<[number, number]>;
    return pointInRing(lng, lat, outer);
  }
  if (geom.type === 'MultiPolygon') {
    const polys = geom.coordinates as number[][][][];
    for (const p of polys) {
      if (p.length === 0) continue;
      const outer = p[0] as Array<[number, number]>;
      if (pointInRing(lng, lat, outer)) return true;
    }
  }
  return false;
};

/** Outer rings of a Polygon / MultiPolygon coordinate tree. */
const outerRingsOfGeometry = (
  geom: { type: string; coordinates: unknown },
): Array<Array<[number, number]>> => {
  if (geom.type === 'Polygon') {
    const rings = geom.coordinates as number[][][];
    const outer = rings[0] as Array<[number, number]> | undefined;
    return outer && outer.length >= 4 ? [outer] : [];
  }
  if (geom.type === 'MultiPolygon') {
    const out: Array<Array<[number, number]>> = [];
    for (const p of geom.coordinates as number[][][][]) {
      const outer = p[0] as Array<[number, number]> | undefined;
      if (outer && outer.length >= 4) out.push(outer);
    }
    return out;
  }
  return [];
};

/**
 * Tile-path counterpart of `candidateIsInsideShell`: the same containment
 * rule applied to GeoJSON features instead of Overpass candidates.
 */
const candidateIsInsideFeature = (
  part: { feature: OsmBuildingsFeature; centroid: { lng: number; lat: number } },
  shell: { feature: OsmBuildingsFeature },
): boolean => {
  if (pointInPolygonOrMulti(part.centroid.lng, part.centroid.lat, shell.feature.geometry)) {
    return true;
  }

  const outers = outerRingsOfGeometry(shell.feature.geometry);
  if (outers.length === 0) return false;

  let inside = 0;
  let total = 0;
  for (const ring of outerRingsOfGeometry(part.feature.geometry)) {
    for (const [lng, lat] of ring) {
      total++;
      if (outers.some((outer) => pointInRing(lng, lat, outer))) inside++;
    }
  }
  return total > 0 && inside / total >= CONTAINMENT_VERTEX_SHARE;
};

/**
 * Fetch a 3×3 grid of OSM Buildings tiles centred on the click,
 * find the building polygon(s) containing the click, and return
 * the resulting tiers.
 *
 * Returns `null` if the API returns no features in the area
 * (under-mapped buildings, OSM Buildings tile generation lag, or
 * a network failure on every tile request) — the caller falls
 * through to Overpass.
 */
async function fetchFromOsmBuildingsTiles(
  lng: number,
  lat: number,
  opts: { signal?: AbortSignal; timeoutMs?: number } = {},
): Promise<OsmBuildingImport | null> {
  const timeoutMs = opts.timeoutMs ?? 8_000;
  const center = lngLatToTile(lng, lat, OSM_BUILDINGS_TILE_ZOOM);

  // 3×3 tile grid — covers ~3.6km across, plenty to enclose any
  // single building plus its parts even if the click happens to
  // be near a tile boundary.
  const tileCoords: Array<{ x: number; y: number; z: number }> = [];
  for (let dx = -1; dx <= 1; dx++) {
    for (let dy = -1; dy <= 1; dy++) {
      tileCoords.push({ x: center.x + dx, y: center.y + dy, z: center.z });
    }
  }

  const notes: string[] = [];
  const features: OsmBuildingsFeature[] = [];
  const fetchedTiles: string[] = [];

  try {
  await Promise.all(
    tileCoords.map(async ({ x, y, z }) => {
      const url = `${OSM_BUILDINGS_TILE_BASE}/${z}/${x}/${y}.json`;
      const ctl = new AbortController();
      const fwd = opts.signal;
      const onAbort = () => ctl.abort();
      if (fwd) {
        if (fwd.aborted) ctl.abort();
        else fwd.addEventListener('abort', onAbort, { once: true });
      }
      const timer = setTimeout(() => ctl.abort(), timeoutMs);
      try {
        const res = await fetch(url, { signal: ctl.signal });
        clearTimeout(timer);
        if (fwd) fwd.removeEventListener('abort', onAbort);
        if (!res.ok) {
          // 404 is normal for ocean / desert tiles — only surface
          // unexpected errors in the diagnostic to keep noise down.
          // 401/403 = OSM Buildings now requires registration for the
          // anonymous endpoint (as of 2024) — fail the whole tile path
          // so Overpass (which has relations) takes over.
          if (res.status === 401 || res.status === 403) {
            notes.push(`${z}/${x}/${y}: HTTP ${res.status} (OSM Buildings requires registration)`);
            throw new Error('OSM_BUILDINGS_AUTH');
          }
          if (res.status !== 404) {
            notes.push(`${z}/${x}/${y}: HTTP ${res.status}`);
          }
          return;
        }
        const json = (await res.json()) as OsmBuildingsTile;
        fetchedTiles.push(`${z}/${x}/${y}`);
        if (Array.isArray(json.features)) {
          features.push(...json.features);
        }
      } catch (e) {
        clearTimeout(timer);
        if (fwd) fwd.removeEventListener('abort', onAbort);
        const msg = (e as Error).message;
        if (msg === 'OSM_BUILDINGS_AUTH') {
          notes.push('OSM Buildings anonymous API blocked — skipping tile path');
          throw e;
        }
        notes.push(`${z}/${x}/${y}: ${msg}`);
      }
    }),
  );
  } catch (e) {
    if ((e as Error).message === 'OSM_BUILDINGS_AUTH') {
      return null;
    }
    throw e;
  }

  if (features.length === 0) {
    return null;
  }

  // Pre-compute bbox + centroid + height/minHeight for every feature.
  type Candidate = {
    feature: OsmBuildingsFeature;
    bbox: { w: number; s: number; e: number; n: number };
    centroid: { lng: number; lat: number };
    height: number;
    minHeight: number;
  };

  const candidates: Candidate[] = [];
  for (const f of features) {
    if (!f.geometry) continue;
    if (f.geometry.type !== 'Polygon' && f.geometry.type !== 'MultiPolygon') {
      continue;
    }
    const bbox = geojsonBbox(f.geometry.coordinates);
    const centroid = geojsonCentroid(f.geometry.coordinates);
    if (!bbox || !centroid) continue;

    const props = f.properties ?? {};
    let height: number = Number(props.height ?? NaN);
    if (!Number.isFinite(height) || height <= 0) {
      // Some features publish only `levels`; fall back to ~3.5m/level.
      const levels = Number(props.levels ?? NaN);
      if (Number.isFinite(levels) && levels > 0) height = levels * 3.5;
    }
    if (!Number.isFinite(height) || height <= 0) continue;

    let minHeight: number = Number(props.minHeight ?? 0);
    if (!Number.isFinite(minHeight) || minHeight < 0) {
      const minLevel = Number(props.minLevel ?? NaN);
      minHeight = Number.isFinite(minLevel) && minLevel > 0
        ? minLevel * 3.5
        : 0;
    }

    candidates.push({ feature: f, bbox, centroid, height, minHeight });
  }

  if (candidates.length === 0) {
    notes.push(`${features.length} features fetched, none had usable height`);
    return null;
  }

  // Find the smallest bbox feature whose polygon contains the click.
  // "Smallest" disambiguates the high-rise from its surrounding podium
  // when both contain the click point.
  let enclosing: Candidate | null = null;
  let enclosingArea = Infinity;
  for (const c of candidates) {
    if (!pointInPolygonOrMulti(lng, lat, c.feature.geometry)) continue;
    const area = (c.bbox.e - c.bbox.w) * (c.bbox.n - c.bbox.s);
    if (area < enclosingArea) {
      enclosingArea = area;
      enclosing = c;
    }
  }

  // No polygon contains the click — operator probably clicked a
  // plaza or street. Bail; Overpass might do better with its
  // larger `around:300m` radius.
  if (!enclosing) {
    notes.push('no feature polygon contains the click');
    return null;
  }

  // Restrict candidates to those contained by the clicked footprint.
  // A padded bbox was used here, which in a dense block admitted the
  // neighbours across a narrow street as extra "tiers" of the clicked
  // building. Containment is what actually distinguishes a setback from
  // a neighbour.
  const accepted: Candidate[] = [];
  for (const c of candidates) {
    if (c !== enclosing && !candidateIsInsideFeature(c, enclosing)) continue;
    accepted.push(c);
  }

  if (accepted.length === 0) {
    return null;
  }

  // Decide which candidates become tiers:
  //   • Elevated parts (minHeight > 0) → real building:part mapping.
  //     Drop the wide ground-to-roof outline so it doesn't swallow them.
  //   • Multiple distinct heights but ALL minHeight=0 → lossy tile data
  //     (OSM Buildings often collapses setbacks). Keep every part so the
  //     stacking reconstruction below can rebuild bases; do NOT collapse
  //     to the enclosing shell alone (that was the Atlantis/Burj bug —
  //     a single fat block, and Overpass never ran because tiles "won").
  //   • Otherwise → single-shell building.
  const anyElevatedPart = accepted.some(
    (c) => c.minHeight > 0 && c !== enclosing,
  );
  const distinctHeights = new Set(
    accepted.map((c) => Math.round(c.height)),
  );
  const lossyMultiTier = !anyElevatedPart && distinctHeights.size >= 2 && accepted.length >= 2;

  let finalSet: Candidate[];
  if (anyElevatedPart) {
    finalSet = accepted.filter((c) => c !== enclosing || c.minHeight > 0);
  } else if (lossyMultiTier) {
    finalSet = accepted;
    notes.push(
      `lossy multi-tier tile data (${distinctHeights.size} heights, all minHeight=0) — keeping parts for reconstruction / Overpass prefer`,
    );
  } else {
    finalSet = [enclosing];
  }

  const polygons: number[][][][] = [];
  const tiers: BuildingTier[] = [];
  const tierBboxes: Array<{ w: number; s: number; e: number; n: number }> = [];
  const tierAreas: number[] = [];
  let maxH = -Infinity;
  let minB = Infinity;
  const osmIdSamples: number[] = [];

  const pushPolygon = (
    poly: number[][][],
    minHeight: number,
    height: number,
    id?: number,
  ) => {
    const outer = poly[0];
    if (!outer || outer.length < 4) return;
    const idx = polygons.length;
    // Keep full Polygon (outer + holes) — do not discard rings.
    polygons.push(poly);
    tiers.push({ polygon_index: idx, min_height: minHeight, height });
    const bb = geojsonBbox(poly);
    tierBboxes.push(bb ?? { w: 0, s: 0, e: 0, n: 0 });
    tierAreas.push(bb ? Math.max(0, (bb.e - bb.w) * (bb.n - bb.s)) : 0);
    if (height > maxH) maxH = height;
    if (minHeight < minB) minB = minHeight;
    if (typeof id === 'number') osmIdSamples.push(id);
  };

  for (const c of finalSet) {
    const geom = c.feature.geometry;
    const osmId = typeof c.feature.id === 'number' ? c.feature.id : undefined;
    if (geom.type === 'Polygon') {
      const rings = geom.coordinates as number[][][];
      if (rings.length > 0) pushPolygon(rings, c.minHeight, c.height, osmId);
    } else {
      // MultiPolygon — one tier entry per polygon (all rings preserved).
      const mp = geom.coordinates as number[][][][];
      for (const p of mp) {
        if (!p?.length) continue;
        pushPolygon(p, c.minHeight, c.height, osmId);
      }
    }
  }

  if (polygons.length === 0) return null;

  // Reconstruct min_height for lossy all-zero multi-tier tiles so the
  // stepped silhouette survives even when Overpass is unavailable.
  if (lossyMultiTier) {
    reconstructZeroBases(tiers, tierBboxes, tierAreas);
    minB = Infinity;
    for (const t of tiers) if (t.min_height < minB) minB = t.min_height;
    notes.push('reconstructed min_height from containment graph');
  }

  const tileHeightMeters = Number.isFinite(maxH) ? maxH : 0;
  const tileBaseMeters = Number.isFinite(minB) ? minB : 0;

  return {
    polygons,
    tiers,
    heightMeters: tileHeightMeters,
    baseHeightMeters: tileBaseMeters,
    source: 'osm-buildings',
    // The tiles publish pre-extracted heights with no indication of
    // whether OSM stated them or OSM Buildings derived them from a storey
    // count, and the base reconstruction above is our own inference. There
    // is nothing here that can honestly be called measured.
    heightProvenance: 'inferred',
    // The tile path has no notion of a structure outline to offer, so it
    // reports only what it imported. Multi-structure selection needs the
    // Overpass path.
    structures: [{
      id: typeof enclosing.feature.id === 'number' ? enclosing.feature.id : 0,
      kind: 'way',
      name: null,
      distanceMeters: 0,
      clicked: true,
      polygons,
      tiers,
      heightMeters: tileHeightMeters,
      baseHeightMeters: tileBaseMeters,
      heightProvenance: 'inferred',
      osmIds: osmIdSamples,
    }],
    diagnostics: {
      endpoint: `${OSM_BUILDINGS_TILE_BASE} (${fetchedTiles.length}/9 tiles)`,
      rawElements: features.length,
      candidateParts: candidates.length,
      acceptedParts: polygons.length,
      enclosingBuilding:
        typeof enclosing.feature.id === 'number' ? enclosing.feature.id : null,
      osmIds: osmIdSamples,
      notes,
    },
  };
}

/**
 * Score how trustworthy an import is for stepped towers.
 * Higher = prefer. Overpass with real min_heights beats lossy tile data.
 */
function importQualityScore(imp: OsmBuildingImport): number {
  const elevated = imp.tiers.filter((t) => t.min_height > 0).length;
  const distinctTops = new Set(imp.tiers.map((t) => Math.round(t.height))).size;
  // Clean single-shell footprint — good enough to skip Overpass.
  if (imp.tiers.length === 1 && imp.heightMeters > 0) return 20;
  let score = imp.tiers.length;
  score += elevated * 10;
  score += Math.min(distinctTops, 8) * 2;
  if (imp.source === 'osm-overpass') score += 5;
  // Lossy OSM Buildings tiles: many tops, every min_height still 0.
  if (distinctTops >= 2 && elevated === 0) score -= 15;
  return score;
}

/**
 * Lift all-zero min_height tiers by stacking smaller footprints on top of
 * larger shorter ones (podium → mid → spire). Same idea as the Mapbox-tile
 * fallback in SpatialObjectsAdminOverlay — needed because OSM Buildings
 * tiles often emit every part with minHeight=0.
 */
function reconstructZeroBases(
  tiers: BuildingTier[],
  bboxes: Array<{ w: number; s: number; e: number; n: number }>,
  areas: number[],
): void {
  if (tiers.length < 2) return;
  if (!tiers.every((t) => t.min_height === 0)) return;

  const centerInside = (
    innerB: { w: number; s: number; e: number; n: number },
    outerB: { w: number; s: number; e: number; n: number },
  ): boolean => {
    const cx = (innerB.w + innerB.e) / 2;
    const cy = (innerB.s + innerB.n) / 2;
    return cx >= outerB.w && cx <= outerB.e && cy >= outerB.s && cy <= outerB.n;
  };

  for (let i = 0; i < tiers.length; i++) {
    const inner = tiers[i];
    const innerArea = areas[i];
    if (!innerArea) continue;
    let bestBase = 0;
    for (let j = 0; j < tiers.length; j++) {
      if (i === j) continue;
      const outer = tiers[j];
      const outerArea = areas[j];
      if (!outerArea || outerArea <= innerArea) continue;
      if (outer.height >= inner.height) continue;
      if (!centerInside(bboxes[i], bboxes[j])) continue;
      if (outer.height > bestBase) bestBase = outer.height;
    }
    if (bestBase > 0) inner.min_height = bestBase;
  }
}

/**
 * Public entry point — orchestrates the OSM Buildings tile path
 * (primary) and the Overpass-via-proxy path (fallback).
 *
 * Both paths return the same `OsmBuildingImport` shape with the
 * `source` field set to indicate which produced the result, so
 * callers don't need to know which one ran.
 */
export async function fetchOsmBuildingParts(
  lng: number,
  lat: number,
  opts: {
    searchRadiusM?: number;
    timeoutMs?: number;
    signal?: AbortSignal;
  } = {},
): Promise<OsmBuildingImport | null> {
  // Prefer Overpass first. As of 2024 the OSM Buildings anonymous tile
  // API returns 403 without registration, and only Overpass returns
  // multipolygon *relations* — which is where complex hotels/towers
  // store most of their building:part geometry. Tiles are a fast
  // fallback when Overpass is down or empty.
  const fromOverpass = await fetchFromOverpass(lng, lat, opts);
  if (fromOverpass && fromOverpass.tiers.length > 0 && importQualityScore(fromOverpass) >= 10) {
    return fromOverpass;
  }

  const fromTiles = await fetchFromOsmBuildingsTiles(lng, lat, {
    signal: opts.signal,
    // Short timeout — tiles either respond quickly or are blocked.
    timeoutMs: Math.min(opts.timeoutMs ?? 8_000, 8_000),
  });

  if (fromTiles && fromOverpass) {
    return importQualityScore(fromOverpass) >= importQualityScore(fromTiles)
      ? fromOverpass
      : fromTiles;
  }
  if (fromOverpass && fromOverpass.tiers.length > 0) return fromOverpass;
  if (fromTiles && fromTiles.tiers.length > 0) return fromTiles;

  return null;
}
